// Loosely based on https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useEventListener/useEventListener.ts
// but supports zod for validation when reading from local storage, and drops a few features
// we don't need.
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import Zod from "zod";

export function useLocalStorage<T>(
  key: string,
  schema: Zod.Schema<T>,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const readValue = useCallback((): T => {
    let strValue: string | null = null;
    try {
      strValue = window.localStorage.getItem(key);
    } catch (error) {
      console.warn(`Error reading localStorage key ${key}:`, error);
    }

    if (strValue === null) {
      return initialValue;
    }

    try {
      return schema.parse(JSON.parse(strValue));
    } catch (error) {
      console.warn(`Error parsing localStorage key ${key}:`, error);
      return initialValue;
    }
  }, [initialValue, key, schema]);

  const writeValue: Dispatch<SetStateAction<T>> = useCallback(
    value => {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(readValue()) : value;

      // Save to local storage. This also fires a 'storage' event for all
      // other tabs.
      try {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      } catch (error) {
        console.warn(`Error writing localStorage key ${key}:`, error);
      }

      // We dispatch a custom event so every similar useLocalStorage hook is notified
      // (on the same tab)
      window.dispatchEvent(new StorageEvent("local-storage", { key }));
    },
    [key, readValue]
  );

  const [storedValue, setStoredValue] = useState(() => {
    return readValue();
  });

  const handleStorageChange = useCallback(
    (event: StorageEvent | Event) => {
      if ((event as StorageEvent).key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [key, readValue]
  );

  // register listeners
  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("local-storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("local-storage", handleStorageChange);
    };
  });

  return [storedValue, writeValue];
}
